import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  Alert,
  AlertBody,
  AlertIcon,
  Button,
  cn,
  ConfirmationDialog,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Pill,
  RiArrowLeftLine,
  RiArrowRightSLine,
  RiCloseLine,
  RiErrorWarningFill,
  RiErrorWarningLine,
  RiMoreLine,
  RiSubtractLine,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DraftPlot, DraftPlotGroup, DraftPlotGroupStatus } from '@/api/rest/resources/types/plot';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';

import { useDraftPlotCommit } from '../hooks/useDraftPlotCommit';
import { useRemoveDraftPlotGroup, useUpdateDraftPlotGroup } from '../hooks/useDraftPlotGroupActions';
import { CommitConfirmationDialog } from './CommitConfirmationDialog';

const SUB_CONTENT_STYLES = cn(
  'z-dialog min-w-[160px] rounded-md bg-white-100 py-2 elevation-1',
  'animate-in fade-in-0 slide-in-from-left-1',
);

const SUB_TRIGGER_CLASS = cn(
  'typography-body1 flex cursor-pointer items-center justify-between gap-2 px-4 py-1.5 text-text-primary outline-none',
  'data-[highlighted]:bg-neutral-black-4',
);

type GroupEditPanelProps = {
  group: DraftPlotGroup;
  plots: DraftPlot[];
  organizationId: string;
  onBack: () => void;
  onDeleted: () => void;
  onSaved: () => void;
  onRefetchGroups: () => void;
  onEditPlot: (plotId: string) => void;
};

export const GroupEditPanel = ({
  group,
  plots,
  organizationId,
  onBack,
  onDeleted,
  onSaved,
  onRefetchGroups,
  onEditPlot,
}: GroupEditPanelProps) => {
  const { t } = useTranslation();
  const isStaff = useIsStaffUser();
  const [groupName, setGroupName] = useState(group.name);
  const [status, setStatus] = useState<DraftPlotGroupStatus>(group.status);
  const [saveError, setSaveError] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [removeError, setRemoveError] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);

  const iconBtnClass = 'text-text-secondary hover:text-text-primary';
  const fieldBoxClass = 'flex flex-col gap-0.5 rounded-xl border border-neutral-black-12 px-4 py-3';
  const deleteLabel = t('shared.uploadPlots.editGroup.menu.delete');

  const { submit: updateGroup, isSubmitting } = useUpdateDraftPlotGroup();
  const { submit: removeGroup, isRemoving } = useRemoveDraftPlotGroup();

  const groupPlots = plots.filter((p) => group.draft_plot_ids.includes(p.id));
  const totalAreaHa = (group.total_area / 10_000).toFixed(2);
  const isReady = status === 'READY';

  const { handleCommit, isCommitting } = useDraftPlotCommit({
    organizationId,
    panelState: 'review',
    hasDraftPlots: groupPlots.length > 0,
  });

  const handleSave = async () => {
    setSaveError(false);
    try {
      await updateGroup(group.id, {
        name: groupName,
        status,
      });
      onSaved();
    } catch {
      setSaveError(true);
    }
  };

  const handleSubmitGroup = async () => {
    setSaveError(false);
    try {
      await updateGroup(group.id, {
        name: groupName,
        status,
      });
      setIsSubmitConfirmOpen(false);
      handleCommit(group.draft_plot_ids);
    } catch {
      setSaveError(true);
      setIsSubmitConfirmOpen(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError(false);
    try {
      await removeGroup(group.id);
      setIsDeleteDialogOpen(false);
      onDeleted();
    } catch {
      setDeleteError(true);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleRemovePlot = async (plotId: string) => {
    setRemoveError(false);
    const remainingIds = group.draft_plot_ids.filter((id) => id !== plotId);
    try {
      if (remainingIds.length === 0) {
        await removeGroup(group.id);
        onDeleted();
        return;
      }
      await updateGroup(group.id, { draft_plot_ids: remainingIds });
      onRefetchGroups();
    } catch {
      setRemoveError(true);
    }
  };

  return (
    <>
      <div className='flex h-full flex-col overflow-hidden'>
        {/* Header */}
        <div className='flex shrink-0 items-center gap-2 border-b border-neutral-black-12 px-4 py-3'>
          <button
            type='button'
            aria-label={t('shared.uploadPlots.editGroup.backAriaLabel')}
            onClick={onBack}
            className='shrink-0 text-text-secondary hover:text-text-primary'
          >
            <RiArrowLeftLine size={18} />
          </button>
          <span className='typography-body1Semibold flex-1 truncate'>{group.name}</span>
          <Pill size='small' className='shrink-0 bg-neutral-black-8 text-text-secondary'>
            {isReady ? t('shared.uploadPlots.editGroup.readyBadge') : t('shared.uploadPlots.editGroup.draftBadge')}
          </Pill>
          <Dropdown>
            <DropdownTrigger aria-label={t('shared.uploadPlots.editGroup.menu.ariaLabel')} className={iconBtnClass}>
              <RiMoreLine size={18} />
            </DropdownTrigger>
            <DropdownContent align='end'>
              <DropdownMenuPrimitive.Sub>
                <DropdownMenuPrimitive.SubTrigger className={SUB_TRIGGER_CLASS}>
                  {t('shared.uploadPlots.editGroup.menu.changeStatus')}
                  <RiArrowRightSLine size={16} className='text-text-secondary' />
                </DropdownMenuPrimitive.SubTrigger>
                <DropdownMenuPrimitive.Portal>
                  <DropdownMenuPrimitive.SubContent className={SUB_CONTENT_STYLES} sideOffset={2}>
                    <DropdownItem onSelect={() => setStatus('DRAFT')}>
                      {t('shared.uploadPlots.editGroup.menu.statusDraft')}
                    </DropdownItem>
                    <DropdownItem onSelect={() => setStatus('READY')}>
                      {t('shared.uploadPlots.editGroup.menu.statusPublish')}
                    </DropdownItem>
                  </DropdownMenuPrimitive.SubContent>
                </DropdownMenuPrimitive.Portal>
              </DropdownMenuPrimitive.Sub>
              <DropdownItem className='text-error' onClick={() => setIsDeleteDialogOpen(true)}>
                {deleteLabel}
              </DropdownItem>
            </DropdownContent>
          </Dropdown>
          <button
            type='button'
            aria-label={t('shared.uploadPlots.editGroup.closeAriaLabel')}
            onClick={onBack}
            className={iconBtnClass}
          >
            <RiCloseLine size={18} />
          </button>
        </div>

        {/* Body */}
        <div className='flex-1 overflow-y-auto px-4 py-4'>
          <Stack spacing={3}>
            {saveError && (
              <Alert type='error'>
                <AlertIcon>
                  <RiErrorWarningLine size={20} />
                </AlertIcon>
                <AlertBody>{t('shared.uploadPlots.editGroup.saveFailed')}</AlertBody>
              </Alert>
            )}
            {deleteError && (
              <Alert type='error'>
                <AlertIcon>
                  <RiErrorWarningLine size={20} />
                </AlertIcon>
                <AlertBody>{t('shared.uploadPlots.editGroup.deleteFailed')}</AlertBody>
              </Alert>
            )}
            {removeError && (
              <Alert type='error'>
                <AlertIcon>
                  <RiErrorWarningLine size={20} />
                </AlertIcon>
                <AlertBody>{t('shared.uploadPlots.editGroup.removePlotFailed')}</AlertBody>
              </Alert>
            )}

            <div className={fieldBoxClass}>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.editGroup.fields.groupName')}
              </span>
              <input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className='typography-body1 w-full bg-transparent outline-none placeholder:text-text-disabled'
              />
            </div>

            {/* Stats row */}
            <div className='grid grid-cols-2 gap-3'>
              <div className={fieldBoxClass}>
                <span className='typography-overline text-text-secondary'>
                  {t('shared.uploadPlots.editGroup.fields.totalArea')}
                </span>
                <span className='typography-body1'>{totalAreaHa} ha</span>
              </div>
              <div className={fieldBoxClass}>
                <span className='typography-overline text-text-secondary'>
                  {t('shared.uploadPlots.editGroup.fields.landscapeArea')}
                </span>
                <span className='typography-body1'>0 ha</span>
              </div>
            </div>

            {/* Plots table */}
            <div>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.editGroup.sections.plots')}
              </span>
              <div className='mt-2 rounded-3xl bg-white-100'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('shared.uploadPlots.editGroup.columns.plots')}</TableHead>
                      <TableHead>{t('shared.uploadPlots.editGroup.columns.type')}</TableHead>
                      <TableHead>{t('shared.uploadPlots.editGroup.columns.size')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupPlots.map((plot) => (
                      <TableRow key={plot.id} className='group/row cursor-pointer' onClick={() => onEditPlot(plot.id)}>
                        <TableCell className='typography-body2'>
                          <Stack direction='row' centerMain spacing={2}>
                            {plot.errors.length > 0 && (
                              <RiErrorWarningFill size={16} className='shrink-0 text-error' data-testid='alert-icon' />
                            )}
                            <span className='truncate'>{plot.name}</span>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Pill size='small' className='bg-neutral-black-8'>
                            {t(`global.plotTypes.${plot.land_type}`)}
                          </Pill>
                        </TableCell>
                        <TableCell className='typography-body2'>{(plot.area / 10_000).toFixed(2)} ha</TableCell>
                        <TableCell>
                          <button
                            type='button'
                            aria-label={t('shared.uploadPlots.editGroup.removePlotAriaLabel')}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePlot(plot.id);
                            }}
                            className='text-text-secondary opacity-0 transition-opacity hover:text-error-100 group-hover/row:opacity-100'
                          >
                            <RiSubtractLine size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Stack>
        </div>

        {/* Footer */}
        <div className='flex shrink-0 items-center justify-between border-t border-neutral-black-12 px-4 py-3'>
          <Button variant='outline' onClick={onBack}>
            {t('global.ui.buttons.cancel')}
          </Button>
          <Button
            loading={isSubmitting || isCommitting}
            onClick={() => (isReady && isStaff ? setIsSubmitConfirmOpen(true) : handleSave())}
          >
            {isReady && isStaff
              ? t('shared.uploadPlots.editGroup.submitButton')
              : t('shared.uploadPlots.editGroup.saveButton')}
          </Button>
        </div>
      </div>

      <CommitConfirmationDialog
        open={isSubmitConfirmOpen}
        onOpenChange={setIsSubmitConfirmOpen}
        plotNames={groupPlots.map((p) => p.name)}
        isSubmitting={isSubmitting || isCommitting}
        onConfirm={handleSubmitGroup}
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        header={deleteLabel}
        body={group.name}
        CancelAction={
          <Button variant='outline' onClick={() => setIsDeleteDialogOpen(false)}>
            {t('global.ui.buttons.cancel')}
          </Button>
        }
        PrimaryAction={
          <Button className='bg-error hover:bg-error-dark' loading={isRemoving} onClick={handleDelete}>
            {deleteLabel}
          </Button>
        }
      />
    </>
  );
};
